import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCredits } from '@/hooks/useCredits';
import { 
  Calculator, 
  Calendar, 
  Image, 
  Video, 
  FileText, 
  Megaphone,
  Sparkles,
  DollarSign
} from 'lucide-react';

interface CostEstimate {
  daily: number;
  weekly: number;
  monthly: number;
  yearly: number;
}

interface UsageSettings {
  postsPerDay: number;
  platforms: number;
  contentTypes: {
    socialPost: boolean;
    blogPost: boolean;
    email: boolean;
    adCopy: boolean;
  };
  features: {
    imageGeneration: boolean;
    imageSearch: boolean;
    videoGeneration: boolean;
    hashtags: boolean;
    audienceAnalysis: boolean;
    trendAnalysis: boolean;
  };
  improvements: number; // percentage of posts that get improved
}

export function CreditCostCalculator() {
  const { costs } = useCredits();
  const [settings, setSettings] = useState<UsageSettings>({
    postsPerDay: 3,
    platforms: 2,
    contentTypes: {
      socialPost: true,
      blogPost: false,
      email: false,
      adCopy: false,
    },
    features: {
      imageGeneration: false,
      imageSearch: true,
      videoGeneration: false,
      hashtags: true,
      audienceAnalysis: false,
      trendAnalysis: false,
    },
    improvements: 20,
  });

  const [estimate, setEstimate] = useState<CostEstimate>({
    daily: 0,
    weekly: 0,
    monthly: 0,
    yearly: 0,
  });

  useEffect(() => {
    if (!costs) return;

    let dailyCost = 0;

    // Calculate content generation costs
    const contentTypeCosts = costs.costs.content.content_types;
    const activeContentTypes = Object.entries(settings.contentTypes)
      .filter(([_, enabled]) => enabled)
      .map(([type]) => type);

    // Posts per day across all platforms
    const totalPostsPerDay = settings.postsPerDay * settings.platforms;

    // Base content generation cost
    activeContentTypes.forEach(type => {
      const costPerPost = contentTypeCosts[type] || costs.costs.content.base_generation;
      dailyCost += totalPostsPerDay * costPerPost;
    });

    // Improvement costs
    const postsToImprove = totalPostsPerDay * (settings.improvements / 100);
    dailyCost += postsToImprove * costs.costs.content.improvement;

    // Feature costs
    if (settings.features.imageGeneration) {
      dailyCost += totalPostsPerDay * costs.costs.media.image.ai_generate;
    } else if (settings.features.imageSearch) {
      dailyCost += totalPostsPerDay * costs.costs.media.image.stock_search;
    }

    if (settings.features.videoGeneration) {
      // Assume 20% of posts include video
      dailyCost += totalPostsPerDay * 0.2 * costs.costs.media.video.ai_generate;
    }

    if (settings.features.hashtags) {
      dailyCost += totalPostsPerDay * costs.costs.processing.hashtag_generation;
    }

    if (settings.features.audienceAnalysis) {
      // Once per week
      dailyCost += costs.costs.processing.audience_analysis / 7;
    }

    if (settings.features.trendAnalysis) {
      // Once per day
      dailyCost += costs.costs.features.trend_analysis;
    }

    // Calculate different time periods
    setEstimate({
      daily: Math.round(dailyCost),
      weekly: Math.round(dailyCost * 7),
      monthly: Math.round(dailyCost * 30),
      yearly: Math.round(dailyCost * 365),
    });
  }, [settings, costs]);

  if (!costs) {
    return <div>Loading cost information...</div>;
  }

  const formatCredits = (credits: number) => {
    return credits.toLocaleString();
  };

  const getCostBreakdown = () => {
    const breakdown = [];
    const contentTypeCosts = costs.costs.content.content_types;
    const totalPostsPerDay = settings.postsPerDay * settings.platforms;

    // Content generation
    Object.entries(settings.contentTypes).forEach(([type, enabled]) => {
      if (enabled) {
        const cost = contentTypeCosts[type] || costs.costs.content.base_generation;
        breakdown.push({
          name: type.replace(/([A-Z])/g, ' $1').trim(),
          dailyCost: totalPostsPerDay * cost,
          unitCost: cost,
        });
      }
    });

    // Improvements
    if (settings.improvements > 0) {
      const postsToImprove = totalPostsPerDay * (settings.improvements / 100);
      breakdown.push({
        name: 'Content Improvements',
        dailyCost: postsToImprove * costs.costs.content.improvement,
        unitCost: costs.costs.content.improvement,
      });
    }

    // Features
    if (settings.features.imageGeneration) {
      breakdown.push({
        name: 'AI Image Generation',
        dailyCost: totalPostsPerDay * costs.costs.media.image.ai_generate,
        unitCost: costs.costs.media.image.ai_generate,
      });
    } else if (settings.features.imageSearch) {
      breakdown.push({
        name: 'Stock Image Search',
        dailyCost: totalPostsPerDay * costs.costs.media.image.stock_search,
        unitCost: costs.costs.media.image.stock_search,
      });
    }

    if (settings.features.hashtags) {
      breakdown.push({
        name: 'Hashtag Generation',
        dailyCost: totalPostsPerDay * costs.costs.processing.hashtag_generation,
        unitCost: costs.costs.processing.hashtag_generation,
      });
    }

    return breakdown;
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="p-6 border-b bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
              <Calculator className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100 mb-1">
                Credit Cost Calculator
              </h3>
              <p className="text-sm text-emerald-700 dark:text-emerald-200">
                Estimate your credit usage based on your content creation patterns
              </p>
            </div>
          </div>
        </div>
        <CardContent className="p-6 space-y-8">
          {/* Enhanced Posting Frequency */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-blue-500" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Publishing Schedule</h4>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200/50">
                <Label htmlFor="posts-per-day" className="flex items-center justify-between mb-4">
                  <span className="font-medium text-blue-900 dark:text-blue-100">Posts per day</span>
                  <Badge variant="outline" className="bg-white/80 text-blue-700 border-blue-200">
                    {settings.postsPerDay}
                  </Badge>
                </Label>
                <Slider
                  id="posts-per-day"
                  min={1}
                  max={20}
                  step={1}
                  value={[settings.postsPerDay]}
                  onValueChange={([value]) => setSettings({ ...settings, postsPerDay: value })}
                  className="w-full"
                />
              </div>

              <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-lg border border-purple-200/50">
                <Label htmlFor="platforms" className="flex items-center justify-between mb-4">
                  <span className="font-medium text-purple-900 dark:text-purple-100">Number of platforms</span>
                  <Badge variant="outline" className="bg-white/80 text-purple-700 border-purple-200">
                    {settings.platforms}
                  </Badge>
                </Label>
                <Slider
                  id="platforms"
                  min={1}
                  max={10}
                  step={1}
                  value={[settings.platforms]}
                  onValueChange={([value]) => setSettings({ ...settings, platforms: value })}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gradient-to-r from-transparent via-gray-200 to-transparent" />

          {/* Enhanced Content Types */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-indigo-500" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Content Types</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg border border-orange-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="social-post" className="cursor-pointer font-medium text-orange-900 dark:text-orange-100">Social Posts</Label>
                  <Switch
                    id="social-post"
                    checked={settings.contentTypes.socialPost}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        contentTypes: { ...settings.contentTypes, socialPost: checked }
                      })
                    }
                    className="data-[state=checked]:bg-orange-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="blog-post" className="cursor-pointer font-medium text-green-900 dark:text-green-100">Blog Posts</Label>
                  <Switch
                    id="blog-post"
                    checked={settings.contentTypes.blogPost}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        contentTypes: { ...settings.contentTypes, blogPost: checked }
                      })
                    }
                    className="data-[state=checked]:bg-green-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="email" className="cursor-pointer font-medium text-blue-900 dark:text-blue-100">Email Campaigns</Label>
                  <Switch
                    id="email"
                    checked={settings.contentTypes.email}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        contentTypes: { ...settings.contentTypes, email: checked }
                      })
                    }
                    className="data-[state=checked]:bg-blue-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-lg border border-purple-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="ad-copy" className="cursor-pointer font-medium text-purple-900 dark:text-purple-100">Ad Copy</Label>
                  <Switch
                    id="ad-copy"
                    checked={settings.contentTypes.adCopy}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        contentTypes: { ...settings.contentTypes, adCopy: checked }
                      })
                    }
                    className="data-[state=checked]:bg-purple-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gradient-to-r from-transparent via-gray-200 to-transparent" />

          {/* Enhanced Features */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Additional Features</h4>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-lg border border-pink-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="image-gen" className="cursor-pointer flex items-center gap-2 font-medium text-pink-900 dark:text-pink-100">
                    <Image className="w-4 h-4" />
                    AI Image Generation
                  </Label>
                  <Switch
                    id="image-gen"
                    checked={settings.features.imageGeneration}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        features: { 
                          ...settings.features, 
                          imageGeneration: checked,
                          imageSearch: checked ? false : settings.features.imageSearch
                        }
                      })
                    }
                    className="data-[state=checked]:bg-pink-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-lg border border-cyan-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="image-search" className="cursor-pointer flex items-center gap-2 font-medium text-cyan-900 dark:text-cyan-100">
                    <Image className="w-4 h-4" />
                    Stock Image Search
                  </Label>
                  <Switch
                    id="image-search"
                    checked={settings.features.imageSearch}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        features: { 
                          ...settings.features, 
                          imageSearch: checked,
                          imageGeneration: checked ? false : settings.features.imageGeneration
                        }
                      })
                    }
                    className="data-[state=checked]:bg-cyan-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-lg border border-red-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="video-gen" className="cursor-pointer flex items-center gap-2 font-medium text-red-900 dark:text-red-100">
                    <Video className="w-4 h-4" />
                    AI Video Generation
                  </Label>
                  <Switch
                    id="video-gen"
                    checked={settings.features.videoGeneration}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        features: { ...settings.features, videoGeneration: checked }
                      })
                    }
                    className="data-[state=checked]:bg-red-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-lg border border-amber-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="hashtags" className="cursor-pointer font-medium text-amber-900 dark:text-amber-100">Hashtag Generation</Label>
                  <Switch
                    id="hashtags"
                    checked={settings.features.hashtags}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        features: { ...settings.features, hashtags: checked }
                      })
                    }
                    className="data-[state=checked]:bg-amber-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg border border-violet-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="audience" className="cursor-pointer font-medium text-violet-900 dark:text-violet-100">Audience Analysis</Label>
                  <Switch
                    id="audience"
                    checked={settings.features.audienceAnalysis}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        features: { ...settings.features, audienceAnalysis: checked }
                      })
                    }
                    className="data-[state=checked]:bg-violet-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg border border-indigo-200/50 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center justify-between">
                  <Label htmlFor="trends" className="cursor-pointer font-medium text-indigo-900 dark:text-indigo-100">Trend Analysis</Label>
                  <Switch
                    id="trends"
                    checked={settings.features.trendAnalysis}
                    onCheckedChange={(checked) => 
                      setSettings({ 
                        ...settings, 
                        features: { ...settings.features, trendAnalysis: checked }
                      })
                    }
                    className="data-[state=checked]:bg-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gradient-to-r from-transparent via-gray-200 to-transparent" />

          {/* Enhanced Content Improvements */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone className="w-5 h-5 text-orange-500" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Content Optimization</h4>
            </div>
            <div className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg border border-orange-200/50">
              <Label htmlFor="improvements" className="flex items-center justify-between mb-4">
                <span className="font-medium text-orange-900 dark:text-orange-100">Content improvements</span>
                <Badge variant="outline" className="bg-white/80 text-orange-700 border-orange-200">
                  {settings.improvements}%
                </Badge>
              </Label>
              <Slider
                id="improvements"
                min={0}
                max={100}
                step={5}
                value={[settings.improvements]}
                onValueChange={([value]) => setSettings({ ...settings, improvements: value })}
                className="w-full mb-3"
              />
              <p className="text-xs text-orange-700 dark:text-orange-300 bg-white/60 dark:bg-orange-900/20 p-2 rounded">
                Percentage of posts that will be refined/improved after initial generation
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Cost Estimates */}
      <Card className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
              <DollarSign className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Estimated Credit Usage
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-200">
                Based on your selected content creation patterns
              </p>
            </div>
          </div>
        </div>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200/50 text-center">
              <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">Daily</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">{formatCredits(estimate.daily)}</p>
              <p className="text-xs text-green-600 dark:text-green-400">credits/day</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-200/50 text-center">
              <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Weekly</p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{formatCredits(estimate.weekly)}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400">credits/week</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-lg border border-purple-200/50 text-center relative">
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-purple-500 text-white text-xs">Popular</Badge>
              </div>
              <p className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">Monthly</p>
              <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{formatCredits(estimate.monthly)}</p>
              <p className="text-xs text-purple-600 dark:text-purple-400">credits/month</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg border border-orange-200/50 text-center">
              <p className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-1">Yearly</p>
              <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">{formatCredits(estimate.yearly)}</p>
              <p className="text-xs text-orange-600 dark:text-orange-400">credits/year</p>
            </div>
          </div>

          <Separator />

          {/* Cost Breakdown */}
          <div>
            <h4 className="font-semibold mb-3">Daily Cost Breakdown</h4>
            <div className="space-y-2">
              {getCostBreakdown().map((item, index) => (
                <div key={index} className="flex justify-between items-center text-sm">
                  <span className="capitalize">{item.name}</span>
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="font-mono">
                      {item.unitCost} per unit
                    </Badge>
                    <span className="font-medium text-right w-20">
                      {formatCredits(Math.round(item.dailyCost))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {costs.packages && costs.packages.length > 0 && (
            <>
              <Separator />
              <div>
                <h4 className="font-semibold mb-3">Recommended Credit Package</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {costs.packages.map((pkg) => {
                    const monthsOfCredits = pkg.credits / estimate.monthly;
                    const isRecommended = monthsOfCredits >= 1 && monthsOfCredits <= 3;
                    
                    return (
                      <Card 
                        key={pkg.credits} 
                        className={cn(
                          "relative",
                          isRecommended ? "border-primary" : ""
                        )}
                      >
                        {pkg.popular && (
                          <Badge className="absolute -top-2 -right-2" variant="default">
                            Popular
                          </Badge>
                        )}
                        <CardContent className="p-4 text-center">
                          <p className="font-bold">{pkg.label}</p>
                          <p className="text-2xl font-bold mt-1">${pkg.price}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            ~{monthsOfCredits.toFixed(1)} months
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </CardContent>
        <div className="h-2 bg-gradient-to-r from-blue-200 via-indigo-200 to-purple-200 dark:from-blue-800 dark:via-indigo-800 dark:to-purple-800" />
      </Card>
    </div>
  );
}

function cn(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}